import React, { useId, useMemo, memo } from 'react';
import { MathJax } from 'better-react-mathjax';

/**
 * 生成内容的哈希值，用于创建唯一的 key
 * @param {string} str - 输入字符串
 * @returns {string} - 哈希值
 */
const hashContent = (str) => {
  if (!str) return 'empty';
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash;
  }
  return Math.abs(hash).toString(36);
};

/**
 * 安全的 MathJax 组件
 *
 * 1. 用「内容 hash + 实例 id」作为 key，内容变化时整体重新挂载，天然触发一次重排；
 * 2. 显式关闭 dynamic：
 *    better-react-mathjax 的 dynamic 默认在开发环境下为 true，其内部 useLayoutEffect
 *    没有依赖数组，会导致每次 render 都执行 typesetClear + typesetPromise。
 *    结果面板交互（点击题目/切换页）会频繁 render，大量重排相互竞态，
 *    组件卸载后 MathJax 仍在校验已脱离文档的节点，抛
 *    "Cannot read properties of null (reading 'nextSibling')" → Typesetting failed。
 *    已有 key 重挂载机制，dynamic 不需要，关掉后每次挂载只重排一次；
 * 3. memo：children 未变化的父级重渲染不再触发本组件 render。
 *
 * @param {Object} props - 传递给 MathJax 的所有属性
 * @param {React.ReactNode} props.children - 要渲染的数学内容
 */
function SafeMathJax({ children, ...props }) {
  const uniqueId = useId();

  // 生成基于内容的唯一 key，确保内容变化时组件完全重新创建
  const contentKey = useMemo(() => {
    return `mathjax-${uniqueId}-${hashContent(String(children))}`;
  }, [uniqueId, children]);

  // 如果内容为空，直接返回 null
  if (!children) {
    return null;
  }

  return (
    <MathJax
      key={contentKey}
      dynamic={false}
      hideUntilTypeset="first"
      {...props}
    >
      {children}
    </MathJax>
  );
}

export default memo(SafeMathJax);
